import React, { useEffect, useMemo } from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  createAssessment,
  updateAssessment,
} from "../redux/actions/assessmentActions";
import { getSubjects } from "../redux/actions/subjectActions";
import { getClassrooms } from "../redux/actions/classroomActions";
import { getTerms } from "../redux/actions/termActions";
import { assessmentActions } from "../redux/slices/assessment";
import { cleanFormData } from "../helpers/formHelpers";

import { AssessmentType } from "../shared/enums/AssessmentType";
import { AssessmentResponseDto } from "../shared/dtos/assessment/AssessmentResponseDto";
import { AssessmentRequestDto } from "../shared/dtos/assessment/AssessmentRequestDto";

const assessmentTypeOptions = Object.values(AssessmentType).map((type) => ({
  value: type,
  label: type.replace(/_/g, " "),
}));

const assessmentSchema = Yup.object().shape({
  name: Yup.string().required("Assessment name is required"),
  type: Yup.mixed<AssessmentType>()
    .oneOf(Object.values(AssessmentType))
    .required("Assessment type is required"),
  subjectId: Yup.string().required("Subject is required"),
  classroomId: Yup.string().required("Classroom is required"),
  termId: Yup.string().required("Term is required"),
  assessmentDate: Yup.string().required("Date is required"),
  maxMarks: Yup.number()
    .required("Max marks is required")
    .min(1, "Must be at least 1"),
  weightage: Yup.number()
    .min(0, "Weightage cannot be negative")
    .max(100, "Cannot exceed 100%")
    .nullable(),
});

interface CreateOrUpdateAssessmentFormProps {
  assessment?: AssessmentResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateAssessmentForm: React.FC<
  CreateOrUpdateAssessmentFormProps
> = ({
  assessment = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const { items: subjects } = useAppSelector((state) => state.subject);
  const { items: classrooms } = useAppSelector((state) => state.classroom);
  const { items: terms } = useAppSelector((state) => state.term);

  useEffect(() => {
    if (!subjects || subjects.length === 0) dispatch(getSubjects());
    if (!classrooms || classrooms.length === 0) dispatch(getClassrooms());
    if (!terms || terms.length === 0) dispatch(getTerms());
  }, [dispatch, subjects, classrooms, terms]);

  const subjectOptions = useMemo(
    () => subjects?.map((s) => ({ value: s.id, label: s.name })) || [],
    [subjects],
  );
  const classroomOptions = useMemo(
    () => classrooms?.map((c) => ({ value: c.id, label: c.name })) || [],
    [classrooms],
  );
  const termOptions = useMemo(
    () => terms?.map((t) => ({ value: t.id, label: t.name })) || [],
    [terms],
  );

  const initialValues: AssessmentRequestDto = {
    name: assessment?.name || "",
    type: (assessment?.type as AssessmentType) || AssessmentType.TEST,
    subjectId: "",
    classroomId: "",
    termId: "",
    assessmentDate:
      assessment?.assessmentDate?.substring(0, 10) ||
      new Date().toISOString().split("T")[0],
    maxMarks: assessment?.maxMarks || 100,
    weightage: assessment?.weightage ?? 100,
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(assessmentActions.setErrorUpdate(null))
            : () => dispatch(assessmentActions.setErrorCreate(null))
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={assessmentSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;
          const cleanedValues: AssessmentRequestDto = {
            name: rawCleaned.name,
            type: values.type,
            subjectId: rawCleaned.subjectId,
            classroomId: rawCleaned.classroomId,
            termId: rawCleaned.termId,
            assessmentDate: rawCleaned.assessmentDate,
            maxMarks: Number(rawCleaned.maxMarks),
            weightage: rawCleaned.weightage
              ? Number(rawCleaned.weightage)
              : null,
          };

          if (isEditing && assessment) {
            dispatch(
              updateAssessment({ id: assessment.id, values: cleanedValues }),
            );
          } else {
            dispatch(createAssessment(cleanedValues));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<AssessmentRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <FormTextField
                name="name"
                label="Assessment Name (e.g., Term 1 Math Opener)"
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormSelectField
                  name="type"
                  label="Assessment Type"
                  options={assessmentTypeOptions}
                />
                <FormSelectField
                  name="subjectId"
                  label="Subject"
                  options={subjectOptions}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormSelectField
                  name="classroomId"
                  label="Classroom"
                  options={classroomOptions}
                />
                <FormSelectField
                  name="termId"
                  label="Term"
                  options={termOptions}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="assessmentDate"
                  label="Assessment Date"
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <FormTextField
                  name="maxMarks"
                  label="Max Marks"
                  type="number"
                  slotProps={{ htmlInput: { min: 1 } }}
                />
                <FormTextField
                  name="weightage"
                  label="Weightage (%)"
                  type="number"
                  slotProps={{ htmlInput: { min: 0, max: 100 } }}
                />
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Assessment"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateAssessmentForm;
